import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PmsResolvedFinancialRiskResponse } from '../../domain/pms-resolved-financial-risk';
import { TaskService } from '../task.service';

export type HtmlReportResolvedFinancialRiskLoadStatus =
   | 'IDLE'
   | 'LOADING'
   | 'LOADED'
   | 'ERROR';

export interface HtmlReportResolvedFinancialRiskStoreState {
   status: HtmlReportResolvedFinancialRiskLoadStatus;
   result: PmsResolvedFinancialRiskResponse | null;
   errorStatus: number | null;
}

const INITIAL_STATE: HtmlReportResolvedFinancialRiskStoreState = {
   status: 'IDLE',
   result: null,
   errorStatus: null
};

/**
 * The API owns monetary eligibility and deduplication. Never infer a resolved
 * amount from locally cached risk status, affected rows or report JSON.
 */
@Injectable({ providedIn: 'root' })
export class HtmlReportResolvedFinancialRiskStoreService {
   private readonly stateSubject =
      new BehaviorSubject<HtmlReportResolvedFinancialRiskStoreState>(INITIAL_STATE);

   readonly state$ = this.stateSubject.asObservable();

   private activeTaskInstanceId: number | null = null;
   private activePmsDataId: number | null = null;
   private loadingKey: string | null = null;
   private requestVersion = 0;

   constructor(private taskService: TaskService) {}

   load(taskInstanceId: number, pmsDataId: number): void {
      if (taskInstanceId <= 0 || pmsDataId <= 0) {
         this.reset();
         return;
      }

      const key = `${taskInstanceId}:${pmsDataId}`;
      if (this.loadingKey === key) {
         return;
      }

      const requestVersion = ++this.requestVersion;
      this.activeTaskInstanceId = taskInstanceId;
      this.activePmsDataId = pmsDataId;
      this.loadingKey = key;

      // Clear the old report's amount immediately, including on refresh.
      this.stateSubject.next({
         status: 'LOADING',
         result: null,
         errorStatus: null
      });

      this.taskService
         .getPmsResolvedFinancialRisk(taskInstanceId, pmsDataId)
         .subscribe({
            next: (result) => {
               if (requestVersion !== this.requestVersion) {
                  return;
               }

               this.loadingKey = null;
               // A mismatched response must never be shown against another report.
               if (!result || result.pmsDataId !== pmsDataId) {
                  this.stateSubject.next({
                     status: 'ERROR',
                     result: null,
                     errorStatus: null
                  });
                  return;
               }

               this.stateSubject.next({
                  status: 'LOADED',
                  result,
                  errorStatus: null
               });
            },
            error: (error: HttpErrorResponse) => {
               if (requestVersion !== this.requestVersion) {
                  return;
               }

               this.loadingKey = null;
               this.stateSubject.next({
                  status: 'ERROR',
                  result: null,
                  errorStatus: error.status
               });
            }
         });
   }

   refresh(): void {
      if (this.activeTaskInstanceId === null || this.activePmsDataId === null) {
         return;
      }

      // Mutation completion supersedes an in-flight pre-mutation GET.
      // The previous response is ignored using requestVersion.
      this.loadingKey = null;
      this.load(this.activeTaskInstanceId, this.activePmsDataId);
   }

   reset(): void {
      ++this.requestVersion;
      this.activeTaskInstanceId = null;
      this.activePmsDataId = null;
      this.loadingKey = null;
      this.stateSubject.next(INITIAL_STATE);
   }
}
